import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, Package } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage({ setIsAuthenticated }) {
  const [isSignup, setIsSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    org_name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isSignup ? `${API}/auth/signup` : `${API}/auth/login`;
      const payload = isSignup
        ? formData
        : { email: formData.email, password: formData.password };

      const response = await axios.post(endpoint, payload);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(isSignup ? 'Account created successfully!' : 'Welcome back!');
      setIsAuthenticated(true);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="bg-primary p-3">
              <Package className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">
              Keeboxs
            </h1>
          </div>
          <p className="text-text-secondary text-lg">
            {isSignup ? 'Create your secure vault' : 'Welcome back'}
          </p>
        </div>

        <div className="bg-white border-2 border-gray-200 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {isSignup && (
              <>
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                    placeholder="John Doe"
                    data-testid="input-full-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Organization Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.org_name}
                    onChange={(e) => setFormData({ ...formData, org_name: e.target.value })}
                    className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                    placeholder="Acme Corp"
                    data-testid="input-org-name"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary"
                placeholder="you@company.com"
                data-testid="input-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-white border-2 border-gray-200 p-3 focus:border-primary focus:outline-none text-text-primary pr-12"
                  placeholder="••••••••"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
              data-testid="btn-submit-auth"
            >
              {loading ? 'Processing...' : isSignup ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsSignup(!isSignup)}
              className="text-primary hover:underline font-medium"
              data-testid="toggle-auth-mode"
            >
              {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </div>

        {/* Demo Credentials */}
        {!isSignup && (
          <div className="mt-6 bg-indigo-50 border-2 border-indigo-200 rounded-lg p-5" data-testid="demo-credentials-box">
            <p className="text-sm font-semibold text-primary mb-3">Try the Demo</p>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, email: 'demo@keeboxs.com', password: 'Demo123!' });
                }}
                className="w-full text-left bg-white border border-indigo-200 rounded-lg px-4 py-2.5 hover:border-primary transition-colors group"
                data-testid="btn-fill-demo-admin"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-text-primary">KeeDemo Corporation</p>
                    <p className="text-xs text-text-muted">demo@keeboxs.com &middot; Admin</p>
                  </div>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium group-hover:bg-primary group-hover:text-white transition-colors">
                    Fill
                  </span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, email: 'sarah@keeboxs.com', password: 'Demo123!' });
                }}
                className="w-full text-left bg-white border border-indigo-200 rounded-lg px-4 py-2.5 hover:border-primary transition-colors group"
                data-testid="btn-fill-demo-member"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-text-primary">Sarah Connor</p>
                    <p className="text-xs text-text-muted">sarah@keeboxs.com &middot; Member</p>
                  </div>
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium group-hover:bg-primary group-hover:text-white transition-colors">
                    Fill
                  </span>
                </div>
              </button>
            </div>
            <p className="text-xs text-text-muted mt-3">Password for all demo accounts: <code className="bg-white px-1.5 py-0.5 rounded font-mono text-primary">Demo123!</code></p>
          </div>
        )}
      </div>
    </div>
  );
}